/**
 * Marketing i18n fragment — comparePages (the public /compare/* pages).
 *
 * These pages exist for one job: a visitor who lands with "isn't this just
 * n8n / enterprise search / ServiceNow?" gets the honest architecture
 * comparison (V4.4: no absolute competitor claims, no capability denial that
 * ages — the difference is where things sit, not what buttons exist).
 *
 * Claims rules apply in full: competitor names appear ONLY here and in
 * "Compare with …" links (enforced by check:integration-matrix's compare
 * scope); never pair a competitor name with integration/connect wording —
 * FixControl does not integrate with these products, it is compared to them.
 */

export const comparePagesEn = {
  shared: {
    eyebrow: "Honest comparison",
    ctaTitle: "See it on one real project",
    ctaBody:
      "Connect your tickets, code or documentation, start read-only, and judge the difference on your own work. Guided onboarding proves each connection before you rely on it.",
    ctaPrimary: "Create your first project",
    ctaSecondary: "Book a demo",
    backLabel: "All comparisons start on the homepage",
  },
  workflowAutomation: {
    meta: {
      title: "FixControl vs. workflow automation (n8n, Zapier)",
      description:
        "Where FixControl differs from workflow builders like n8n and Zapier: assembled project context, central rules outside the flow, verification before the decision, and a record of every approval.",
    },
    title: "Is this not just workflow automation?",
    intro1:
      "Partly — and the partly is exactly the risk. Tools like n8n and Zapier are excellent workflow builders: you build flows that execute steps, including an approval button as one of the steps. For recurring tasks without judgment they are hard to beat.",
    intro2:
      "The difference is not in the flow, but in what sits around it. A workflow tool executes steps someone designed in advance. FixControl first assembles the project's information — the ticket, the code, earlier reports — and then applies central rules, verification and a recorded decision to the proposed work.",
    diffsTitle: "Three things that sit outside every flow",
    diffs: {
      d1: {
        title: "Rules outside the workflow",
        body:
          "In a workflow builder, an approval step is something you add — and therefore can forget, change or delete per flow. In FixControl the rules apply centrally, across all work; a step cannot be skipped because someone forgot to draw it.",
      },
      d2: {
        title: "Verification before the decision",
        body:
          "A proposed code change is tested in an isolated environment first. The reviewer decides on test results, not on trust.",
      },
      d3: {
        title: "A record of every decision",
        body:
          "Not execution logs, but a decision record: who approved what, on which grounds, under which version of the rules — frozen at decision time and exportable with a signature.",
      },
    },
    whenTitle: "When a workflow builder is enough",
    whenBody:
      "Are you happy with n8n or Zapier for recurring tasks without judgment? Keep using them. FixControl is for the actions where a judgment and a signature belong: answers to your customers, changes to your code, deployments. (A direct n8n coupling is not something FixControl offers today.)",
  },
  enterpriseSearch: {
    meta: {
      title: "FixControl vs. enterprise search and AI assistants",
      description:
        "Where FixControl differs from enterprise search and generic AI assistants: information bounded per project, prepared work instead of only answers, and checks plus approval before anything leaves.",
    },
    title: "Is this not just enterprise search with AI?",
    intro1:
      "A search layer or generic AI assistant does something genuinely useful: it searches the sources it can reach and formulates an answer. If finding information were the whole problem, that would be enough.",
    intro2:
      "FixControl starts where the answer stops. It bounds information per project — a mailbox, channels, repositories, documentation you choose — and uses it to prepare actual work: an answer to a customer of your organization, a tested code change, a deployment step. And nothing meaningful leaves without checks and a recorded approval.",
    diffsTitle: "Three differences that matter",
    diffs: {
      d1: {
        title: "Bounded per project, not everything it can reach",
        body:
          "An assistant answers from all the sources it has access to. FixControl works per project with exactly the sources you attached — the invoicing question is answered with invoicing information, not with everything your organization ever wrote.",
      },
      d2: {
        title: "Prepared work, not only answers",
        body:
          "The output is not a chat message but a draft reply, a tested pull request or a prepared deployment step — ready for someone on your team to review.",
      },
      d3: {
        title: "Checks and approval before anything leaves",
        body:
          "A search layer has no opinion about what happens with the answer. FixControl checks the rules and requires a recorded decision before an answer reaches your customer or a change reaches your repository.",
      },
    },
    whenTitle: "When a search layer is enough",
    whenBody:
      "If your only problem is finding information — and no AI acts in your ticket system, repositories or deployments — a search layer or assistant serves you well. The moment AI starts preparing answers and changes in those systems, you need boundaries and approval around it.",
  },
  servicenow: {
    meta: {
      title: "FixControl vs. ServiceNow",
      description:
        "Where FixControl differs from ServiceNow: a layer over your existing tools instead of a platform your work migrates to, and built for AI-prepared work with verification and approval per action.",
    },
    title: "Does ServiceNow not already do this?",
    intro1:
      "ServiceNow is a platform: it wants your work to move to ServiceNow, and it governs what happens inside that platform. For human IT processes its change management is mature and proven.",
    intro2:
      "FixControl differs on two points. It is a layer, not a platform: your tickets stay in Freshdesk, Jira, Odoo or TOPdesk, and changes arrive in GitHub or GitLab as ordinary pull requests — there is no new system to migrate to. And it is built for AI-prepared work: proposals that are tested, bounded and reviewed per action, not for routing human tasks.",
    diffsTitle: "Two architectural differences",
    diffs: {
      d1: {
        title: "A layer over your tools, not a destination",
        body:
          "Your team keeps working in the systems it already uses. FixControl brings the relevant information together per project and adds rules, verification and approval on top — without an implementation project of months.",
      },
      d2: {
        title: "Built for AI-prepared actions",
        body:
          "Every AI proposal — an answer, a code change, a deployment step — is individually tested where applicable, checked against your rules, and decided on by someone on your team. That per-action shape is the product, not an add-on module.",
      },
      d3: {
        title: "First result in days, not quarters",
        body:
          "Two systems and one set of rules is enough to start: read along, investigate, prepare. Expansion follows validation, step by step.",
      },
    },
    whenTitle: "When ServiceNow is enough",
    whenBody:
      "Does your whole organization already run on ServiceNow, and does AI act only inside that platform? Then its own controls may serve you well. Most organizations run ten systems side by side — then you need a layer that fits over them.",
  },
};

export const comparePagesNl = {
  shared: {
    eyebrow: "Eerlijke vergelijking",
    ctaTitle: "Bekijk het op één echt project",
    ctaBody:
      "Koppel je tickets, code of documentatie, begin met meekijken, en beoordeel het verschil op je eigen werk. De begeleide onboarding bewijst elke koppeling voordat je erop bouwt.",
    ctaPrimary: "Maak je eerste project",
    ctaSecondary: "Plan een demo",
    backLabel: "Alle vergelijkingen beginnen op de homepage",
  },
  workflowAutomation: {
    meta: {
      title: "FixControl vs. workflow-automatisering (n8n, Zapier)",
      description:
        "Waarin FixControl verschilt van workflowbouwers zoals n8n en Zapier: samengebrachte projectinformatie, centrale regels buiten de flow, verificatie vóór het besluit, en vastlegging van elke goedkeuring.",
    },
    title: "Is dit niet gewoon workflow-automatisering?",
    intro1:
      "Gedeeltelijk — en dat gedeeltelijke is precies het risico. Tools zoals n8n en Zapier zijn uitstekende workflowbouwers: je bouwt er flows mee die stappen uitvoeren, inclusief een goedkeuringsknop als tussenstap. Voor terugkerende taken zonder oordeel zijn ze moeilijk te verslaan.",
    intro2:
      "Het verschil zit niet in de flow, maar in wat eromheen staat. Een workflowtool voert stappen uit die iemand vooraf heeft ontworpen. FixControl brengt eerst de informatie van het project samen — het ticket, de code, eerdere meldingen — en past daarna centrale regels, verificatie en een vastgelegde beslissing toe op het voorgestelde werk.",
    diffsTitle: "Drie dingen die búíten elke flow staan",
    diffs: {
      d1: {
        title: "Regels buiten de werkstroom",
        body:
          "In een workflowbouwer is een goedkeuringsstap iets dat je toevoegt — en dus per flow kunt vergeten, wijzigen of verwijderen. In FixControl gelden de regels centraal, over al het werk; een stap kan niet worden overgeslagen omdat iemand hem vergat te tekenen.",
      },
      d2: {
        title: "Verificatie vóór het besluit",
        body:
          "Een voorgestelde codewijziging wordt eerst getest in een afgeschermde omgeving. De reviewer beslist op testresultaten, niet op vertrouwen.",
      },
      d3: {
        title: "Vastlegging van elke beslissing",
        body:
          "Geen uitvoeringslogs, maar een besluitoverzicht: wie keurde wat goed, op welke gronden, onder welke versie van de regels — bevroren op het moment van beslissen en ondertekend te exporteren.",
      },
    },
    whenTitle: "Wanneer een workflowbouwer volstaat",
    whenBody:
      "Gebruik je n8n of Zapier naar tevredenheid voor terugkerende taken zonder oordeel? Blijven doen. FixControl is er voor de handelingen waar een oordeel en een handtekening bij horen: antwoorden aan je klanten, wijzigingen in je code, deployments. (Een directe n8n-koppeling heeft FixControl vandaag niet.)",
  },
  enterpriseSearch: {
    meta: {
      title: "FixControl vs. enterprise search en AI-assistenten",
      description:
        "Waarin FixControl verschilt van enterprise search en generieke AI-assistenten: informatie begrensd per project, voorbereid werk in plaats van alleen antwoorden, en controle plus goedkeuring voordat er iets vertrekt.",
    },
    title: "Is dit niet gewoon enterprise search met AI?",
    intro1:
      "Een zoeklaag of generieke AI-assistent doet iets oprecht nuttigs: hij doorzoekt de bronnen waar hij bij kan en formuleert een antwoord. Als informatie vinden het hele probleem was, zou dat genoeg zijn.",
    intro2:
      "FixControl begint waar het antwoord ophoudt. Het begrenst informatie per project — een mailbox, kanalen, repositories en documentatie die je kiest — en gebruikt die om echt werk voor te bereiden: een antwoord aan een klant van je organisatie, een geteste codewijziging, een deployment-stap. En er vertrekt niets betekenisvols zonder controle en een vastgelegde goedkeuring.",
    diffsTitle: "Drie verschillen die ertoe doen",
    diffs: {
      d1: {
        title: "Begrensd per project, niet alles waar hij bij kan",
        body:
          "Een assistent antwoordt vanuit alle bronnen waartoe hij toegang heeft. FixControl werkt per project met precies de bronnen die je koppelde — de facturatievraag wordt beantwoord met facturatie-informatie, niet met alles wat je organisatie ooit opschreef.",
      },
      d2: {
        title: "Voorbereid werk, niet alleen antwoorden",
        body:
          "De uitkomst is geen chatbericht maar een conceptantwoord, een geteste pull request of een voorbereide deployment-stap — klaar om door iemand van je team beoordeeld te worden.",
      },
      d3: {
        title: "Controle en goedkeuring voordat er iets vertrekt",
        body:
          "Een zoeklaag heeft geen mening over wat er met het antwoord gebeurt. FixControl controleert de regels en vereist een vastgelegde beslissing voordat een antwoord je klant bereikt of een wijziging je repository.",
      },
    },
    whenTitle: "Wanneer een zoeklaag volstaat",
    whenBody:
      "Is je enige probleem informatie vinden — en handelt er geen AI in je ticketsysteem, repositories of deployments — dan ben je met een zoeklaag of assistent goed geholpen. Zodra AI antwoorden en wijzigingen in die systemen gaat voorbereiden, heb je grenzen en goedkeuring eromheen nodig.",
  },
  servicenow: {
    meta: {
      title: "FixControl vs. ServiceNow",
      description:
        "Waarin FixControl verschilt van ServiceNow: een laag over je bestaande tools in plaats van een platform waar je werk naartoe verhuist, en gebouwd voor AI-voorbereid werk met verificatie en goedkeuring per actie.",
    },
    title: "Doet ServiceNow dit niet al?",
    intro1:
      "ServiceNow is een platform: het wil dat je werk naar ServiceNow toe komt, en het bewaakt wat er binnen dat platform gebeurt. Voor menselijke IT-processen is dat change management volwassen en bewezen.",
    intro2:
      "FixControl verschilt op twee punten. Het is een laag, geen platform: je tickets blijven in Freshdesk, Jira, Odoo of TOPdesk, en wijzigingen komen in GitHub of GitLab binnen als gewone pull requests — er is geen nieuw systeem om naartoe te migreren. En het is gebouwd voor AI-voorbereid werk: voorstellen die per stuk getest, begrensd en beoordeeld worden, niet voor het routeren van menselijke taken.",
    diffsTitle: "Twee architectuurverschillen",
    diffs: {
      d1: {
        title: "Een laag over je tools, geen bestemming",
        body:
          "Je team blijft werken in de systemen die het al gebruikt. FixControl brengt per project de relevante informatie samen en legt daar regels, verificatie en goedkeuring overheen — zonder implementatietraject van maanden.",
      },
      d2: {
        title: "Gebouwd voor AI-voorbereide acties",
        body:
          "Elk AI-voorstel — een antwoord, een codewijziging, een deployment-stap — wordt waar van toepassing per stuk getest, langs je regels gelegd en door iemand van je team beslist. Die vorm per actie ís het product, geen extra module.",
      },
      d3: {
        title: "Eerste resultaat in dagen, niet in kwartalen",
        body:
          "Twee systemen en één set regels is genoeg om te beginnen: meekijken, onderzoeken, voorbereiden. Uitbreiden volgt op validatie, stap voor stap.",
      },
    },
    whenTitle: "Wanneer ServiceNow volstaat",
    whenBody:
      "Draait je hele organisatie al op ServiceNow, en handelt AI alleen dáárbinnen? Dan kunnen de eigen controles van dat platform volstaan. De meeste organisaties gebruiken tien systemen naast elkaar — dan heb je een laag nodig die daaroverheen past.",
  },
};
